import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { GlassNav } from '../../components/layout/GlassNav';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useAuth } from '../../hooks/useAuth';
import { login } from '../../services/auth';
import { ApiClientError } from '../../services/api';
import { GOOGLE_OAUTH_URL } from '../../lib/constants';
import { isValidEmail, minLength } from '../../lib/validation';

export function LoginPage() {
  const { loginSuccess } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!isValidEmail(email)) {
      setError('Enter a valid email.');
      return;
    }
    if (!minLength(password, 8)) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const auth = await login({ email: email.trim(), password });
      loginSuccess(auth);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.fieldErrors && err.fieldErrors.length > 0) {
          const errors: Record<string, string> = {};
          err.fieldErrors.forEach((fe) => {
            errors[fe.field] = fe.message;
          });
          setFieldErrors(errors);
          setError('Please fix the errors below.');
        } else if (err.code === 'EMAIL_NOT_VERIFIED') {
          setError(err.message + ' Check your email for the verification link.');
        } else if (err.code === 'ACCOUNT_LOCKED') {
          setError(err.message);
        } else {
          setError(err.message);
        }
      } else {
        setError('Unable to sign in. Try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-surface text-on-surface">
      <GlassNav />
      <div className="mx-auto flex max-w-md flex-col gap-8 px-6 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Welcome back</CardTitle>
            <CardDescription>Sign in with your Mopion account.</CardDescription>
          </CardHeader>
          <form className="space-y-6" onSubmit={submit}>
            <div>
              <Input
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {fieldErrors.email && (
                <p className="mt-1 text-xs text-error">{fieldErrors.email}</p>
              )}
            </div>
            <div>
              <div className="relative">
                <Input
                  label="Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="absolute right-4 top-11 text-sm font-semibold text-primary hover:text-secondary"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-error">{fieldErrors.password}</p>
              )}
            </div>
            
            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm font-semibold text-secondary hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            
            {error ? <p className="text-sm font-semibold text-error">{error}</p> : null}
            <Button type="submit" className="w-full" loading={loading}>
              Sign in
            </Button>
          </form>
          <div className="mt-6 space-y-4 text-center text-sm">
            <a className="block w-full" href={GOOGLE_OAUTH_URL}>
              <Button variant="secondary" className="w-full" type="button">
                Continue with Google
              </Button>
            </a>
            <p className="text-on-surface/70">
              Need an account?{' '}
              <Link className="font-bold text-secondary hover:underline" to="/register">
                Register
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}
